const { find, remove } = require('lodash');

/**
 * We should only have one iframe loaded (receiving events) at a time.
 * Use setIframeControl and releaseIframeControl to handle cases where there are more than one.
 */

class IframeEventHandler {
    constructor($window) {
        this.listenerObjects = [];
        this.$window = $window;
        this.isListenerActive = false;
        this.onMessageEvent = (event) => { this.messageEventCallback(event); };
        this.activeIframeId = undefined;
    }

    /**
     * Sets an iframe to be active and restrict all events to this unique Id
     * @param {String} uniqueId Unique Id to identify the iframe
     */
    setIframeControl(uniqueId) {
        this.activeIframeId = uniqueId;
    }

    /**
     * Releases the control of the iframe events from the active iframe
     */
    releaseIframeControl() {
        this.activeIframeId = null;
    }

    /**
     * Initialise message event listener.
     */
    init() {
        if (!this.isListenerActive) {
            this.$window.addEventListener('message', this.onMessageEvent);
            this.isListenerActive = true;
        }
    }

    /**
     * Remove message event listener.
     */
    dispose() {
        this.$window.removeEventListener('message', this.onMessageEvent);
        this.isListenerActive = false;
        this.listenerObjects = [];
    }

    /**
     * Callback which processes message event callback.
     * @param {Object} event
     */
    messageEventCallback(event) {
        // TODO:
        // When receiving a message, the event's origin should be validated.
        // However for testing this can be omitted.
        // if (event.origin != 'https://woolworths-domain') {
        //     return
        // }

        const response = JSON.parse(JSON.stringify(event.data, null, 2));
        const eventId = response.eventId;
        this.listenerObjects
            .filter(callbackObject => callbackObject.eventId === eventId)
            .filter(callbackObject => !this.activeIframeId || callbackObject.uniqueId === this.activeIframeId)
            .map(callbackObject => callbackObject.callback)
            .forEach(callback => callback(response));
    }

    /**
     * Register listener.
     * @param {String} eventId
     * @param {Function} listenerCallback
     * @param {String} uniqueId
     */
    on(eventId, listenerCallback, uniqueId = null) {
        if (!this.isListenerActive) this.init();

        // Ensure listener does not already exist for the given event.
        const listenerExists = find(this.listenerObjects, listenerObject => listenerObject.eventId === eventId
            && listenerObject.callback === listenerCallback);

        if (!listenerExists) {
            this.listenerObjects.push({
                eventId,
                callback: listenerCallback,
                uniqueId,
            });
        }
    }

    /**
     * Unregister listener.
     * @param {String} eventId
     * @param {Function} listenerCallback
     */
    off(eventId, listenerCallback) {
        remove(this.listenerObjects, listenerObject => listenerObject.eventId === eventId
            && listenerObject.callback === listenerCallback);

        if (this.listenerObjects.length === 0) {
            this.dispose();
        }
    }

    /**
     * Emits a message to the iframe.
     * @param {Element} iframeElement
     * @param {Object} message - must have eventId e.g. { eventId: 'pay-frame-submit' }
     */
    emit(iframeElement, message) {
        // Send the message to the iframe's content window. The second argument specifies what the origin of the iframe URL is expected to be.
        // Currently for testing it is set to *, indicating no preference. In production this should be set to the production domain of the iframe.
        // For more information, see this page: https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
        if (!iframeElement) {
            return;
        }
        // Be a little tolerant, try find the iframe element
        if (iframeElement[0]) {
            iframeElement = iframeElement[0];
        }
        if (iframeElement.contentWindow) {
            iframeElement.contentWindow.postMessage(message, '*');
        }
    }
}

module.exports = IframeEventHandler;



// WEBPACK FOOTER //
// ./src/wallet/digital-pay-iframe.eventHandler.js